import { formatDate, formatCurrency } from '@/utils/format';
import { formatPhone } from '@/lib/utils';
import { formatStatusLabel } from '@/lib/statusLabels';
import { formatTempoProcuraLabel } from '@/lib/qualificacaoLabels';
import type { Cliente } from '@/features/clientes/types/cliente';
import type { Agendamento, AgendamentoStatus } from '@/features/agendamentos/types/agendamento';

export interface PrintField {
  label: string;
  value: string;
}

const AGENDAMENTO_STATUS: Record<AgendamentoStatus, string> = {
  PENDENTE: 'Pendente',
  CONFIRMADO: 'Confirmado',
  REALIZADO: 'Realizado',
  CANCELADO: 'Cancelado',
  FALTOU: 'Faltou',
  NOVA_TENTATIVA: 'Nova Tentativa',
  REAGENDADO: 'Reagendado',
};

const ESTADO_CIVIL: Record<string, string> = {
  solteiro: 'Solteiro(a)',
  casado: 'Casado(a)',
  divorciado: 'Divorciado(a)',
  viuvo: 'Viúvo(a)',
  uniao_estavel: 'União estável',
};

const PRIORIDADE: Record<string, string> = {
  ALTA: 'Alta',
  MEDIA: 'Média',
  BAIXA: 'Baixa',
  APENAS_PESQUISANDO: 'Apenas pesquisando',
  baixa: 'Baixa',
  media: 'Média',
  alta: 'Alta',
};

const TEMPERATURA: Record<string, string> = {
  QUENTE: 'Quente',
  MEDIO: 'Médio',
  FRIO: 'Frio',
  frio: 'Frio',
  morno: 'Morno',
  quente: 'Quente',
};

function dash(value: unknown): string {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'boolean') return value ? 'Sim' : 'Não';
  return String(value);
}

function fmtDate(value: unknown): string {
  if (!value) return '—';
  return formatDate(String(value)) || '—';
}

function fmtPhone(value: unknown): string {
  if (!value) return '—';
  const formatted = formatPhone(String(value));
  return formatted || String(value);
}

function fmtMoney(value: unknown): string {
  if (value === null || value === undefined || value === '') return '—';
  const n = Number(value);
  if (Number.isNaN(n)) return String(value);
  return formatCurrency(n);
}

function joinAddress(c: Cliente): string {
  const parts = [
    c.logradouro,
    c.numero ? `nº ${c.numero}` : null,
    c.complemento,
    c.bairro,
    c.cidade,
    c.estado,
    c.cep ? `CEP ${c.cep}` : null,
  ].filter(Boolean);
  return parts.length ? parts.join(', ') : '—';
}

/** Remove campos vazios ou irrelevantes para impressão compacta */
export function compactPrintFields(fields: PrintField[]): PrintField[] {
  return fields.filter((f) => {
    const v = f.value?.trim();
    if (!v || v === '—') return false;
    if (v === 'Não' || v === 'R$ 0,00') return false;
    if (f.label === 'Nacionalidade' && v.toLowerCase() === 'brasileira') return false;
    if (f.label === 'Ligações realizadas' && v === '0') return false;
    return true;
  });
}

export function buildClientePrintFields(cliente: Cliente): PrintField[] {
  const isPJ = cliente.cpf && cliente.cpf.replace(/\D/g, '').length > 11;

  const fields: PrintField[] = [
    { label: 'Nome', value: dash(cliente.nome) },
    { label: isPJ ? 'CNPJ' : 'CPF', value: dash(cliente.cpf) },
    { label: 'E-mail', value: dash(cliente.email) },
    { label: 'Telefone 1', value: fmtPhone(cliente.tel1) },
    { label: 'Telefone 2', value: fmtPhone(cliente.tel2) },
    { label: 'Data de nascimento', value: fmtDate(cliente.data_nascimento) },
    { label: 'RG', value: dash(cliente.rg) },
    { label: 'Emissão RG', value: fmtDate(cliente.data_emissao_rg) },
    { label: 'Naturalidade', value: dash(cliente.naturalidade) },
    { label: 'Nacionalidade', value: dash(cliente.nacionalidade) },
    { label: 'Atividade desenvolvida', value: dash(cliente.atividade_desenvolvida) },
    { label: 'Rendimento titular', value: dash(cliente.rendimento_titular) },
  ];

  if (isPJ) {
    fields.push(
      { label: 'Razão social', value: dash(cliente.razao_social) },
      { label: 'Inscrição estadual', value: dash(cliente.inscricao_estadual) },
      { label: 'Responsável', value: dash(cliente.nome_responsavel) },
      { label: 'CPF responsável', value: dash(cliente.cpf_responsavel) },
      { label: 'E-mail responsável', value: dash(cliente.email_responsavel) },
      { label: 'Tel. responsável', value: fmtPhone(cliente.tel_responsavel) }
    );
  }

  fields.push(
    { label: 'Endereço', value: joinAddress(cliente) },
    {
      label: 'Estado civil',
      value: ESTADO_CIVIL[cliente.estado_civil] || dash(cliente.estado_civil),
    },
    { label: 'Nome do cônjuge', value: dash(cliente.nome_conjuge) },
    { label: 'CPF do cônjuge', value: dash(cliente.cpf_conjuge) },
    { label: 'Nasc. cônjuge', value: fmtDate(cliente.data_nascimento_conjuge) },
    { label: 'Nome da mãe', value: dash(cliente.nome_mae) },
    { label: 'Nome do pai', value: dash(cliente.nome_pai) },
    { label: 'Banco', value: dash(cliente.banco) },
    { label: 'Agência', value: dash(cliente.agencia) },
    { label: 'Conta', value: dash(cliente.conta) },
    { label: 'Tipo de conta', value: dash(cliente.tipo_conta) },
    { label: 'Tem entrada', value: dash(cliente.tem_entrada) },
    { label: 'Tipo de bem', value: dash(cliente.tipo_bem) },
    { label: 'Valor entrada', value: fmtMoney(cliente.valor_entrada) },
    { label: 'Parcela orçamento', value: fmtMoney(cliente.valor_parcela_orcamento) },
    { label: 'Tempo procurando', value: formatTempoProcuraLabel(cliente.tempo_procura) },
    {
      label: 'Prioridade',
      value: cliente.prioridade
        ? PRIORIDADE[cliente.prioridade] || String(cliente.prioridade)
        : '—',
    },
    { label: 'Tempo aluguel', value: dash(cliente.tempo_aluguel) },
    { label: 'Cliente qualificado', value: dash(cliente.cliente_qualificado) },
    {
      label: 'Temperatura do lead',
      value: cliente.temperatura_lead
        ? TEMPERATURA[cliente.temperatura_lead] || String(cliente.temperatura_lead)
        : '—',
    },
    { label: 'Ligações realizadas', value: dash(cliente.ligacoes_realizadas) },
    { label: 'Última ligação', value: fmtDate(cliente.ultima_ligacao) },
    { label: 'Primeiro contato', value: fmtDate(cliente.data_primeiro_contato) },
    { label: 'Status cadastro', value: dash(cliente.status_cadastro) },
    { label: 'Observações', value: dash(cliente.observacoes) }
  );

  return compactPrintFields(fields);
}

export function buildAgendamentoPrintFields(ag: Agendamento): PrintField[] {
  return compactPrintFields([
    { label: 'Data', value: fmtDate(ag.data_agendamento) },
    { label: 'Horário', value: ag.hora_agendamento?.slice(0, 5) || '—' },
    {
      label: 'Status',
      value: AGENDAMENTO_STATUS[ag.status] || dash(ag.status),
    },
    { label: 'Relacionador', value: dash(ag.relacionador?.nome) },
    { label: 'Líder de vendas', value: dash(ag.lider_vendas?.nome) },
    { label: 'Observações', value: dash(ag.observacoes) },
  ]);
}

export function formatAgendamentoStatus(status: string): string {
  return AGENDAMENTO_STATUS[status as AgendamentoStatus] || status || '—';
}

export function formatContratoMoney(value: unknown): string {
  return fmtMoney(value);
}

export const DOCUMENTO_TIPO_LABELS: Record<string, string> = {
  documento_com_foto: 'Documento com Foto',
  comprovante_residencia: 'Comprovante de Residência',
  comprovante_pagamento: 'Comprovante de Pagamento',
  declaracoes: 'Declarações',
  comprovante_renda: 'Comprovante de Renda',
  geral: 'Anexo',
};

function getDocumentoTipoLabel(tipo?: string): string {
  if (!tipo) return 'Anexo';
  return DOCUMENTO_TIPO_LABELS[tipo] || tipo.replace(/_/g, ' ');
}

/** Lista tipos de documento anexados (sem nome de arquivo) */
export function buildAnexosPrintFields(
  documentos: Array<{ tipo?: string; tipo_documento?: string; created_at?: string }>
): PrintField[] {
  const byTipo = new Map<string, PrintField>();

  documentos.forEach((doc) => {
    const tipoKey = doc.tipo_documento || doc.tipo || 'geral';
    const label = getDocumentoTipoLabel(tipoKey);
    const date = doc.created_at ? fmtDate(doc.created_at) : '—';

    const existing = byTipo.get(tipoKey);
    if (!existing) {
      byTipo.set(tipoKey, { label, value: date });
      return;
    }

    if (date !== '—' && existing.value === '—') {
      byTipo.set(tipoKey, { label, value: date });
    }
  });

  return Array.from(byTipo.values());
}

function formatBemDescricao(bens: Record<string, unknown> | null | undefined): string {
  if (!bens) return '—';
  const parts: string[] = [];
  if (bens.tipo_bem) parts.push(String(bens.tipo_bem));
  const marcaModelo = [bens.marca, bens.modelo].filter(Boolean).join(' ');
  if (marcaModelo) parts.push(marcaModelo);
  if (bens.codigo_bem != null) parts.push(`Cód. ${bens.codigo_bem}`);
  return parts.length ? parts.join(' · ') : '—';
}

function formatGrupoDescricao(grupo: Record<string, unknown> | null | undefined): string {
  if (!grupo) return '—';
  const parts: string[] = [];
  if (grupo.especie) parts.push(String(grupo.especie));
  if (grupo.numero_grupo != null) parts.push(`Nº ${grupo.numero_grupo}`);
  if (grupo.titulo) parts.push(String(grupo.titulo));
  return parts.length ? parts.join(' · ') : '—';
}

/** Campos de contrato para impressão — bem, grupo, valores e status */
export function buildContratoPrintFields(
  contrato: Record<string, unknown>,
  statusLabel: string
): PrintField[] {
  const bens =
    (contrato.bens as Record<string, unknown> | null | undefined) ||
    (contrato.bem as Record<string, unknown> | null | undefined);
  const grupo =
    (contrato.grupo as Record<string, unknown> | null | undefined) ||
    (contrato.grupos as Record<string, unknown> | null | undefined);

  const precoBem = bens?.preco_bem ?? contrato.valor_bem;
  const valorParcela = contrato.valor_parcela;

  return compactPrintFields([
    { label: 'Status', value: formatStatusLabel(statusLabel) },
    { label: 'Bem', value: formatBemDescricao(bens) },
    { label: 'Preço do bem', value: fmtMoney(precoBem) },
    { label: 'Grupo', value: formatGrupoDescricao(grupo) },
    { label: 'Cota', value: dash(contrato.cota) },
    { label: 'Parcela', value: fmtMoney(valorParcela) },
    {
      label: 'P. pagas',
      value: contrato.parcelas_pagas != null ? String(contrato.parcelas_pagas) : '—',
    },
    { label: 'Assembleia', value: fmtDate(contrato.data_assembleia) },
    {
      label: 'Consultor',
      value: dash((contrato.consultor as { nome?: string } | null)?.nome),
    },
  ]);
}
